/**
 * LiveTestingDrawBodyField 컴포넌트
 *
 * Draw on Body 타입을 위한 Live Testing 필드
 * 캔버스 기반 간단한 드로잉 기능 제공
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { MdUndo, MdDelete, MdBrush, MdCircle } from 'react-icons/md';
import classNames from 'classnames';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import LiveTestingContainer from './LiveTestingContainer';

export interface DrawBodyValue {
  imageData?: string; // Base64 이미지 데이터
}

interface LiveTestingDrawBodyFieldProps {
  value: DrawBodyValue;
  onChange: (value: DrawBodyValue) => void;
  backgroundImageUrl?: string | null;
  disabled?: boolean;
}

interface Point {
  x: number;
  y: number;
}

const COLORS = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#000000'];
const BRUSH_SIZES = [2, 4, 8, 12];
const DEFAULT_BACKGROUND_COLOR = '#F9FAFB';
const localeCodeMap: Partial<Record<string, SystemLocaleCodes>> = {
  english: 'en',
  korean: 'ko',
  japanese: 'ja',
  spanish: 'es',
  french: 'fr',
  arabic: 'ar',
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });

const drawImageCover = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const scale = Math.max(canvasWidth / img.width, canvasHeight / img.height);
  const drawWidth = img.width * scale;
  const drawHeight = img.height * scale;
  const dx = (canvasWidth - drawWidth) / 2;
  const dy = (canvasHeight - drawHeight) / 2;
  ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
};

const safeGetImageData = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement
): ImageData | null => {
  try {
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
};

/**
 * Live Testing용 Draw on Body 필드
 */
function LiveTestingDrawBodyField({
  value,
  onChange,
  backgroundImageUrl,
  disabled = false,
}: LiveTestingDrawBodyFieldProps) {
  const { currentLocale } = LiveTestingContainer.useContainer();
  const forcedLocaleCode = currentLocale ? localeCodeMap[currentLocale.toLowerCase()] : undefined;
  const { sltStr } = useStaticLocales({ localeCode: forcedLocaleCode });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState<Point | null>(null);
  const [color, setColor] = useState(COLORS[0]);
  const [brushSize, setBrushSize] = useState(BRUSH_SIZES[1]);
  const [history, setHistory] = useState<ImageData[]>([]);

  const fillDefaultBackground = useCallback((ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    ctx.fillStyle = DEFAULT_BACKGROUND_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // 캔버스 초기화
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const initializeCanvas = async () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (value?.imageData) {
        try {
          const img = await loadImage(value.imageData);
          drawImageCover(ctx, img, canvas.width, canvas.height);
        } catch {
          if (!backgroundImageUrl) {
            fillDefaultBackground(ctx, canvas);
          }
        }
      } else if (!backgroundImageUrl) {
        fillDefaultBackground(ctx, canvas);
      }

      const initialState = safeGetImageData(ctx, canvas);
      setHistory(initialState ? [initialState] : []);
    };

    initializeCanvas().catch(() => undefined);
  }, [backgroundImageUrl, fillDefaultBackground]);

  // 캔버스 데이터 저장
  const saveCanvasData = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let imageData: string;
    try {
      imageData = canvas.toDataURL('image/png');
    } catch {
      return;
    }
    onChange({ imageData });
  }, [onChange]);

  // 히스토리에 현재 상태 저장
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentState = safeGetImageData(ctx, canvas);
    if (!currentState) return;
    setHistory((prev) => [...prev.slice(-19), currentState]); // 최대 20개 유지
  }, []);

  // 좌표 계산
  const getCoordinates = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ('touches' in e) {
        const touch = e.touches[0];
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      }

      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  // 선 그리기
  const drawLine = useCallback(
    (start: Point, end: Point) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    },
    [color, brushSize]
  );

  // 마우스/터치 시작
  const handleStart = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      e.preventDefault();

      const point = getCoordinates(e);
      if (!point) return;

      setIsDrawing(true);
      setLastPoint(point);

      // 점 찍기
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.beginPath();
          ctx.arc(point.x, point.y, brushSize / 2, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
        }
      }
    },
    [disabled, getCoordinates, color, brushSize]
  );

  // 마우스/터치 이동
  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing || disabled) return;
      e.preventDefault();

      const point = getCoordinates(e);
      if (!point || !lastPoint) return;

      drawLine(lastPoint, point);
      setLastPoint(point);
    },
    [isDrawing, disabled, getCoordinates, lastPoint, drawLine]
  );

  // 마우스/터치 종료
  const handleEnd = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      setLastPoint(null);
      saveToHistory();
      saveCanvasData();
    }
  }, [isDrawing, saveToHistory, saveCanvasData]);

  // Undo
  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const newHistory = history.slice(0, -1);
    const prevState = newHistory[newHistory.length - 1];

    if (prevState) {
      ctx.putImageData(prevState, 0, 0);
      setHistory(newHistory);
      saveCanvasData();
    }
  }, [history, saveCanvasData]);

  // Clear
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const clearCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!backgroundImageUrl) {
        fillDefaultBackground(ctx, canvas);
      }
      const clearedState = safeGetImageData(ctx, canvas);
      setHistory(clearedState ? [clearedState] : []);
      onChange({ imageData: undefined });
    };

    clearCanvas();
  }, [backgroundImageUrl, fillDefaultBackground, onChange]);

  return (
    <div className="flex flex-col gap-4">
      {/* 툴바 */}
      <div className="flex flex-wrap items-center gap-4 p-3 bg-white border border-gray-200 rounded-lg">
        {/* 색상 선택 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">
            {sltStr({ key: 'v2-live-testing-draw-color' })}:
          </span>
          <div className="flex gap-1">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={classNames(
                  'w-6 h-6 rounded-full border-2 transition-all',
                  color === c ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                )}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>

        {/* 브러시 크기 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">
            {sltStr({ key: 'v2-live-testing-draw-size' })}:
          </span>
          <div className="flex gap-1">
            {BRUSH_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className={classNames(
                  'w-8 h-8 flex items-center justify-center rounded border transition-all',
                  brushSize === size
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
                onClick={() => setBrushSize(size)}
                disabled={disabled}
              >
                <MdCircle size={size + 4} className="text-gray-600" />
              </button>
            ))}
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
            onClick={handleUndo}
            disabled={disabled || history.length <= 1}
          >
            <MdUndo size={16} />
            {sltStr({ key: 'v2-live-testing-draw-undo' })}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
            onClick={handleClear}
            disabled={disabled}
          >
            <MdDelete size={16} />
            {sltStr({ key: 'v2-live-testing-draw-clear' })}
          </button>
        </div>
      </div>

      {/* 캔버스 */}
      <div
        ref={containerRef}
        className={classNames(
          'relative border-2 border-dashed rounded-lg overflow-hidden',
          disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'
        )}
      >
        {backgroundImageUrl && (
          <img
            src={backgroundImageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none z-0"
            draggable={false}
          />
        )}
        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          className="relative z-10 w-full h-auto cursor-crosshair touch-none"
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />

        {/* 힌트 */}
        {!value?.imageData && !backgroundImageUrl && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-gray-400">
              <MdBrush size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">{sltStr({ key: 'v2-live-testing-draw-hint' })}</p>
            </div>
          </div>
        )}
      </div>

      {/* 안내 텍스트 */}
      <p className="text-xs text-gray-500">
        {sltStr({ key: 'v2-live-testing-draw-help' })}
      </p>
    </div>
  );
}

export default memo(LiveTestingDrawBodyField);
